import { UserSecurityInfoModel } from "../../../models/authentication/token.models";
import { CacheClientService, CacheLocation } from "@rightship-tech/common-ui/services/basic/client-caching.service";
import { autoinject } from "aurelia-dependency-injection";
import { RouteConfig, Router } from 'aurelia-router';
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { NotificationClientService } from "../../../services/basics/notification-client.service";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { ClaimsStep } from "../../../pipelines/authentication/claims.pipeline";
import { Messages } from "../../../configuration/event-messages.constants";
import { EventAggregator, Subscription } from "aurelia-event-aggregator";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { computedFrom } from "aurelia-binding";
import { ActivityLoggerComposer } from "../../../views/components/activity-logger/activity-logger-composer";
import { OnboardingAdminService } from '../../../services/onboarding/onboarding-admin.service';
import { AuthorizeStep } from "../../../pipelines/authentication/authorize.pipeline";
import { GenesisRouterConstants } from "../../../configuration/router.constants";
import { TokenClient } from "../../../services/authentication/token-client.service";
import { NoAuthUrls } from "@rightship-tech/common-ui/constants/authentication/no-auth-urls";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { RightShipQiRouterConstants } from "../../../configuration/rightshipqi-router.constants";
import { ManageRightShipUsersClientService } from "../../../services/modules/righship-qi-manage-users.service";
import { DataStewardClientService } from "../../../services/modules/data-steward-client.service";
import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { GoogleAnalyticsHelper } from '@rightship-tech/common-ui/helpers/google-analytics.helper';
import * as moment from "moment";
import { AppInsightsService } from "@rightship-tech/common-ui/services/appinsights/appinsights-service";
import * as DOMPurify from "dompurify";
import { SignalFunctions } from "../../../services/basics/signal-functions";
import { ScheduledMaintenanceService } from "../../../services/basics/scheduled-maintenance.service";
import { ScheduledMaintenance } from "../../../models/basics/scheduled-maintenance.models";
import { RightShipDialogServiceConstants } from "../../../constants/basics/rightship-dialog.constants";
import { RightshipQiMessages } from "../../../configuration/rightshipqi-event-messages.constants";
import { CustomerDetailsModel } from "../../../models/customers/customer.models";
import { CustomerService } from "../../../services/modules/customer-client.service";
import { PlatformFeatures } from "@rightship-tech/common-ui/dialogs/upgrade-from-freemium/freemium-feature-info-model";
import { Reflection } from "../../../utilities/reflection";
import { UrlHelper } from "../../../helpers/url-helper";
import { RouteGenerator } from "@rightship-tech/common-ui/route-generator";
import { AllEntityTypeConfigurationConstants } from "../../../models/basics/basics.models";
import * as Enumerable from "linq";
import { ConfigService } from "@rightship-tech/common-ui/services/basic/config.service";
import { SignalClientService } from "@rightship-tech/common-ui/services/basic/signal-client.service";
import { IntercomService } from "@rightship-tech/common-ui/services/analytics/intercom.service";
import { AppcuesService } from "@rightship-tech/common-ui/services/analytics/appcues.service";
import { DataDogService } from "@rightship-tech/common-ui/services/analytics/data-dog.service";

//Load styles
require("../../../styles/core.scss");

@autoinject()
export class Layout {

    public appEnvironment: string;
    public showDatePicker: boolean = false;
    public scheduledMaintenance: ScheduledMaintenance;
    public scheduledMaintenanceInterval: any;
    public maintenanceUrl: string;
    public isMaintenanceInProgress: boolean = false;
    private subscriptions: Subscription[] = [];
    private isMfaLogout: boolean = false;
    private isMfaLoggingOut: boolean = false;
    public customerDetails: CustomerDetailsModel;
    public currentRoute: RouteConfig;

    @computedFrom('scheduledMaintenance')
    get scheduledMaintenanceStartDate() {
        if (!this.scheduledMaintenance || !this.scheduledMaintenance.startDate)
            return '';
        return moment(this.scheduledMaintenance.startDate).format('DD MMM YYYY HH:mm');
    }

    @computedFrom('scheduledMaintenance')
    get scheduledMaintenanceEndDate() {
        if (!this.scheduledMaintenance || !this.scheduledMaintenance.endDate)
            return '';
        return moment(this.scheduledMaintenance.endDate).format('DD MMM YYYY HH:mm');
    }

    @computedFrom('scheduledMaintenance', 'isMaintenanceInProgress')
    get isScheduledMaintenanceInProgress() {
        if (!this.scheduledMaintenance || !this.scheduledMaintenance.startDate || this.scheduledMaintenance.isCompleted)
            return false;

        const now = moment(new Date());
        return moment(this.scheduledMaintenance.startDate).isBefore(now) && !this.scheduledMaintenance.isCompleted;
    }

    checkMfaLogout() {
        if (this.isMfaLogout && !this.isMfaLoggingOut) {
            this.isMfaLoggingOut = true;
            setTimeout(() => {
                this.dialogClientService.show(RightShipDialogServiceConstants.URLs.MfaNoticeDialog, null, false, false, false);
            });

            return true;
        }
        return false;
    }

    constructor(
        private routeGenerator: RouteGenerator,
        private cacheClient: CacheClientService,
        private userClientService: UserClientService,
        private tokenClient: TokenClient,
        private router: Router,
        private dataDogService: DataDogService,
        private userClaimsService: UserClaimsService,
        private notificationService: NotificationClientService,
        private activityLoggerComposer: ActivityLoggerComposer,
        private lookupClientService: LookupClientService,
        private onboardingAdminService: OnboardingAdminService,
        private dialogClientService: DialogClientService,
        private signalClientService: SignalClientService,
        private eventAggregator: EventAggregator,
        private manageRightShipUsersClientService: ManageRightShipUsersClientService,
        private dataStewardClientService: DataStewardClientService,
        private googleAnalyticsService: GoogleAnalyticsService,
        private appInsightsService: AppInsightsService,
        private scheduledMaintenanceService: ScheduledMaintenanceService,
        private customerService: CustomerService,
        private configService: ConfigService,
        private intercomService: IntercomService,
        private appcuesService: AppcuesService) {

        this.appEnvironment = this.configService.config.environment;
        if (this.userClientService.currentUser) {
            this.dataStewardClientService.loadMdrMappings();
        }

        this.activityLoggerComposer.register({
            viewModel: 'views/components/activity-logger/reminder',
            icon: 'reminder',
            modelInitiator: () => ({ date: '' })
        });

        this.lookupClientService.clearAllLookups();
        this.documentClick = e => {
            if (!(<any>e).propagationStopped) {
                eventAggregator.publish(Messages.documentClick, { source: "document" });
            }
        };

        this.paste = (e: any) => {
            if (!e.propagationStopped) {
                const items = ((e.clipboardData || (e.originalEvent || {}).clipboardData) || {}).items || [];
                for (const item of items) {
                    if (item.kind === 'file') {
                        const blob = item.getAsFile();
                        const reader = new FileReader();
                        reader.onload = (f) => {
                            eventAggregator.publish(RightshipQiMessages.paste, { source: (<any>f.target).result, fileName: blob.name });
                        };
                        reader.readAsDataURL(blob);
                    }
                }
            }
        };

        userClientService.subscribeOnUserUpdate(() => {
            window.location.reload();
        });

        this.onboardingAdminService.getODataModels().then(x => {
            if (x.entityConfigs) {
                x.entityConfigs.forEach(config => {
                    AllEntityTypeConfigurationConstants.push({
                        claims: [],
                        navbar: "onboardingrequests",
                        editRoute: GenesisRouterConstants.RouteNames.OnboardingRequest,
                        uri: config.uri,
                        title: config.title,
                        entityType: config.entityTypeId,
                        typeSingular: config.typeSingular,
                        isVisible: true
                    });
                });
            }
        }).catch(() => { });

        this.subscriptions.push(eventAggregator.subscribe(Messages.showDatePicker, () => {
            this.showDatePicker = true;
        }));

        this.subscriptions.push(eventAggregator.subscribe(Messages.hideDatePicker, () => {
            this.showDatePicker = false;
        }));

        //Freemium click sub
        this.subscriptions.push(eventAggregator.subscribe(Messages.unlockFeature, x => {
            this.dialogClientService.show(RightShipDialogServiceConstants.URLs.UpgradeFromFreemiumDialog, PlatformFeatures.filter(a => a.key == x)[0]);
        }));

        this.subscriptions.push(this.eventAggregator.subscribe(this.onCoachmarkTriggered, aggregatorData => {
            this.currentCoachmark = aggregatorData.split('_')[0];
        }));

        this.setupWorkflowActivitiesNodes();

        //Claims
        window["isDev"] = this.appEnvironment.toLowerCase() == 'development';
    }

    private onCoachmarkTriggered = 'onCoachmarkTriggered';
    public currentCoachmark: string;

    private setupWorkflowActivitiesNodes() {
        window['conditional-activity'] = { color: "#37ade7", fontColor: "white" }
        window['reassign-activity'] = { color: "#ff6c4f", fontColor: "white" }
        window['error-activity'] = { color: "#51d44a", fontColor: "white" }
    }

    attached() {
        this.setCurrentRoute();
        this.subscriptions.push(this.eventAggregator.subscribe("router:navigation:complete",
            () => {

                this.setCurrentRoute();
                if (this.dialogClientService.isModalVisible && !this.isMfaLoggingOut)
                    this.dialogClientService.hide();

                this.googleAnalyticsService.sendCustomGA4Event('page_view');
                this.appInsightsService.track();

                if (NoAuthUrls.every(url => location.href.toLowerCase().indexOf(url) == -1)) {
                    if (this.isMfaLogout) {
                        this.checkMfaLogout();
                    }
                }
            }));

        this.dialogClientService.subscribeShow(this.showDialogueCallback);
        GoogleAnalyticsHelper.subscribeEvents(this.subscriptions, this.googleAnalyticsService, this.eventAggregator);
        this.subscriptions.push(this.eventAggregator.subscribe("tab-changed", tab => {

            tab = (typeof tab.title == "function" ? tab.title() : tab.title);
            tab = this.stripHtml(tab);

            this.googleAnalyticsService.sendCustomGA4Event('page_view', window.location.pathname + ' | Tab:' + tab);

        }));

        this.subscriptions.push(this.eventAggregator.subscribe("router:navigation:processing", () => {
            this.eventAggregator.publish('onCoachmarkClosed');
        }));

        document.addEventListener("click", this.documentClick);
        document.addEventListener("paste", this.paste);
    }

    private setCurrentRoute() {
        this.currentRoute = Enumerable.from<RouteConfig>(this.routeGenerator.getRoutes()).firstOrDefault(x =>
            this.router.currentInstruction && x.name == this.router.currentInstruction.config.name);
    }

    stripHtml(html) {
        // Create a new div element
        const temporalDivElement = document.createElement("div");
        // Set the HTML content with the providen
        temporalDivElement.innerHTML = DOMPurify.sanitize(html);
        // Retrieve the text property of the element (cross-browser support)
        return temporalDivElement.textContent || temporalDivElement.innerText || "";
    }

    public showDialogueCallback: (view, model, showCloseButton, fullscreen, classList: string) => void = () => {
        this.googleAnalyticsService.sendCustomGA4Event('page_view', 'showDialogueCallback app');
    }

    detached() {
        if (document) {
            document.removeEventListener("click", this.documentClick);
            document.removeEventListener("paste", this.paste);
        }
        this.dialogClientService.unSubscribeShow(this.showDialogueCallback);
        this.subscriptions.forEach(x => {
            x.dispose();
        });
        this.subscriptions = [];
        this.signalClientService.unsubscribe(SignalFunctions.logoutUser, this.onLogoutUser);
        this.signalClientService.unsubscribe(SignalFunctions.scheduledMaintenancesChanged, this.getNextScheduledMaintenance);
        this.signalClientService.unsubscribe(SignalFunctions.scheduledMaintenanceForceStart, this.getNextScheduledMaintenance);
        this.signalClientService.unsubscribe(SignalFunctions.displayNotification, this.displayNotification);
        this.signalClientService.unsubscribe(SignalFunctions.updateMfaSettings, this.updateMfaSettings);
        this.signalClientService.unsubscribe(SignalFunctions.permissionsChanged, this.onPermissionsChanged);
    }

    public documentClick: EventListenerOrEventListenerObject;
    public paste: EventListenerOrEventListenerObject;

    configureRouter(config, router: Router) {
        this.router = router;
        config.title = "RightShip";
        config.options.pushState = true;
        config.options.hashChange = true;
        config.addPipelineStep("authorize", AuthorizeStep);
        config.addPipelineStep("preActivate", ClaimsStep);
        config.mapUnknownRoutes({ redirect: '/statuscode404' });
        config.map(this.routeGenerator.getRoutes());
        this.routeGenerator.setRouter(router);
        router.configure(config);
    }

    async activate() {
        this.eventAggregator.publish("set-header", { backTitle: "Support", class: '' });

        this.signalClientService.subscribe(SignalFunctions.logoutUser, this.onLogoutUser);
        this.signalClientService.subscribe(SignalFunctions.scheduledMaintenancesChanged, this.getNextScheduledMaintenance);
        this.signalClientService.subscribe(SignalFunctions.scheduledMaintenanceForceStart, this.getNextScheduledMaintenance);
        this.signalClientService.subscribe(SignalFunctions.displayNotification, this.displayNotification);
        this.signalClientService.subscribe(SignalFunctions.updateMfaSettings, this.updateMfaSettings);
        this.signalClientService.subscribe(SignalFunctions.permissionsChanged, this.onPermissionsChanged);

        try {
            const res = await this.userClientService.getMe()
            await this.onNewUserSet(res)
            await this.checkPrivacyPolicy(res)
            this.getNextScheduledMaintenance();
        }
        catch (e) {
            console.log(e);
            if (location.href.toLowerCase().indexOf("/resetpassword") == -1) {
                if (e?.content?.details == "[PASSWORD_EXPIRED]") {
                    UrlHelper.redirectForPasswordExpiry();
                }
            }

            if (NoAuthUrls.every(url => location.href.toLowerCase().indexOf(url) == -1)) {
                if (this.userClientService.currentUser)
                    this.onLogoutUser({ userId: this.userClientService.currentUser.id });
            }
        }
    }

    //Update Intercom
    updateIntercom() {
        this.intercomService.update();
    }

    async checkPrivacyPolicy(user: UserSecurityInfoModel) {
        const cachedPrivacyPolicyAcceptance = await this.cacheClient.get('userPrivatePolicy_' + this.configService.config.version + "_" + user.id, CacheLocation.LocalStorage, false);
        if (!cachedPrivacyPolicyAcceptance) {
            //check for privacy policy
            const privacyPolicyAcceptance = await this.manageRightShipUsersClientService.getUserPrivacyPolicyAgreement();
            if (!privacyPolicyAcceptance) {
                if (window.location.href.indexOf(RightShipQiRouterConstants.RouteNames.TermsAndConditions) == -1) {
                    const referrer = window.location.href;
                    window.location.href = RightShipQiRouterConstants.RouteNames.TermsAndConditions + "?referrer=" + referrer;
                }
            } else {
                this.cacheClient.put('userPrivatePolicy_' + this.configService.config.version + "_" + user.id, privacyPolicyAcceptance, CacheLocation.LocalStorage);
            }
        }
    }

    onLogoutUser = (params: { userId: number }) => {
        if (this.userClientService?.currentUser?.id == params.userId) {
            this.tokenClient.logout().then(() => {
                //clear tab cache
                this.cacheClient.remove("tabs-activities");
            });
        }
    }

    @computedFrom(Reflection.getPropertyPath<Layout>(z => z.userClientService.currentUser))
    get isAuthenticated() {
        return this.userClientService.currentUser != null && window.location.href.indexOf(RightShipQiRouterConstants.RouteNames.TermsAndConditions) == -1;
    }

    setUserForDataDog(response: UserSecurityInfoModel) {
        this.dataDogService.setUser({
            id: response.id,
            name: response.userName,
            email: response.email
        });
    }

    senitize(html: string) {
        const div = document.createElement('div');
        div.innerHTML = DOMPurify.sanitize(html);
        const text = div.textContent;
        div.remove();
        return text;
    }

    async onNewUserSet(response: UserSecurityInfoModel) {
        const x = await this.manageRightShipUsersClientService.getMe();

        window["rightshipUser"] = x;
        if (x.mfaLogout) {
            this.isMfaLogout = true;
        }

        //get the claims
        this.userClaimsService.setUserClaims(response.allowedClaims);
        this.customerDetails = CustomerDetailsModel.getCustomerDetailModelFromRSUser(x);
        this.userClientService.currentUser = response;

        //Init Intercom
        this.intercomService.init(x.user, x.intercomHash);

        //This sends data to AppCues
        this.appcuesService.identify();

        //DataDog
        this.setUserForDataDog(response);
    }

    getNextScheduledMaintenance = () => {
        this.scheduledMaintenanceService.getNextScheduledMaintenance().then(x => {
            if (!x)
                return;
            this.scheduledMaintenance = x.scheduledMaintenance;
            this.maintenanceUrl = x.maintenanceWebsiteUrl;

            if (this.scheduledMaintenanceInterval)
                clearInterval(this.scheduledMaintenanceInterval);

            if (!this.scheduledMaintenance)
                return;

            this.scheduledMaintenanceInterval = setInterval(this.tryStartMaintenance, 30000);

        }).catch(() => {
            this.scheduledMaintenance = null;
        });
    }

    displayNotification = (params) => {
        if (params && params.userId && this.userClientService.currentUser && params.userId == this.userClientService.currentUser.id) {
            this.notificationService.show(params.title, params.description, params.notificationType, params.durationMs);
        }
    }

    onPermissionsChanged = (data) => {
        const user = this.userClientService.currentUser;
        if (user && data && data.departmentIds && data.departmentIds.length > 0 && data.departmentIds.indexOf(user.departmentId) > -1) {
            this.userClientService.reloadUser();
        }
    }

    //For users who are already logged in, let's update their mfa settings to not force them to log out
    updateMfaSettings = (params) => {
        const user = this.userClientService.currentUser;
        if (user && user.id && params.userIds.indexOf(user.id) > -1) {
            this.manageRightShipUsersClientService.updateUserMfaSettings();
        }
    }

    tryStartMaintenance = () => {
        const startDate = moment(this.scheduledMaintenance.startDate);
        const now = moment(new Date());

        if (startDate.isAfter(now))
            return;

        this.isMaintenanceInProgress = true;

        if (this.scheduledMaintenance.allowRedirection) {
            this.scheduledMaintenance = null;
            location.replace(this.maintenanceUrl + '?referalUrl=' + location.href);
        }

        if (this.scheduledMaintenanceInterval)
            clearInterval(this.scheduledMaintenanceInterval);
    }

    public async sendBuyFullVersionRequest() {
        await this.customerService.sendBuyFullVersionRequest(this.customerDetails.customerId);

        this.dialogClientService.show(RightShipDialogServiceConstants.URLs.PurchaseRequestDialog,
            {
                title: 'RightShip Sales Team Contacted',
                description: 'Your request has been sent to the RightShip sales team. You will be contacted shortly.'
            });
    }

    @computedFrom('configService.config.urls.rightStoreUrl')
    get rightstoreUrl() {

        //This is done because in appsettings only appsettings.production we don't pass the ?env=production query string
        let concatination = "";
        if (this.appEnvironment.toLowerCase() != "production") {
            concatination = "env=" + this.appEnvironment + "&";
        }
        return this.configService.config.urls.rightStoreUrl + "/Catalog/products?" + concatination + "utm_source=platform&utm_medium=layout&utm_campaign=rightstore";
    }

    //Checks if RightSTORE banner cookie is set on user's browsers
    isRightStoreBannerCookieSet() {
        if (document.cookie.includes("rightstore-cookie-hidden")) {
            return false
        }
        return true;
    }

    //Hide navigation layout DEL-14026 & DEL-15062 - This is to hide the nvagation, banners and search for specifically the RFQ of RS Inspection and Video page
    get hideNavigationLayout(): boolean {
        return window.location.href.includes('/rfq/rfq-add-rightship-inspection') || window.location.href.includes('/video');
    }

}
